import { ArrowForward } from 'assets/icons';
import Modal from 'components/common/Modal';
import ScrollToError from 'components/common/ScrollToError';
import InlineSelect from 'components/inputs/InlineSelect';
import { Field, Form, Formik, type FormikHelpers } from 'formik';
import { useCallback, memo, useState } from 'react';
import Button from 'components/common/Button';
import BuyingHouse from 'assets/images/quick-calculator/buying-house.svg?react';
import BuildingHouse from 'assets/images/quick-calculator/building-house.svg?react';
import { useHistory } from 'react-router-dom';
import { routePatterns } from 'router/route-paths';
import { toast } from 'react-toastify';
import useChangeProjectType from 'hooks/project/useChangeProjectType';
import equal from 'fast-deep-equal/es6/react';
import type { IntlShape } from 'react-intl';
import { messageOf } from 'utils/error';
import type { ILandValuationFormValues, ILandValuationTypeFormValues } from './types';
import { toProjectType } from './utils';

interface IChangeProjectTypeArgs {
  type: string | null;
}

interface ILandValuationTypeModalProps {
  hideModal: () => void;
  isVisible: boolean;
  t: IntlShape['formatMessage'];
  formValuesRef?: { current?: ILandValuationFormValues };
  onSubmit: (
    values: ILandValuationTypeFormValues,
    actions: FormikHelpers<ILandValuationTypeFormValues>,
  ) => Promise<void>;
}

export const useChangeProjectTypeHandler = (
  projectId: string,
): [(args: IChangeProjectTypeArgs) => Promise<void>, boolean] => {
  const changeProjectType = useChangeProjectType();
  const [isSubmitting, setSubmitting] = useState(false);
  const history = useHistory();

  const handleChange = useCallback(
    async ({ type }: IChangeProjectTypeArgs) => {
      setSubmitting(true);
      try {
        await changeProjectType({ projectId, type: toProjectType(type) });
        history.push(routePatterns.projectDetailsCalculator.stringify({ id: projectId }));
      } catch (error) {
        toast.error(messageOf(error));
      } finally {
        setSubmitting(false);
      }
    },
    [changeProjectType, history, projectId],
  );

  return [handleChange, isSubmitting];
};

const LandValuationTypeModal = ({
  hideModal,
  isVisible,
  t,
  formValuesRef = {},
  onSubmit,
}: ILandValuationTypeModalProps) => (
  <Modal close={hideModal} isVisible={isVisible}>
    <Formik<ILandValuationTypeFormValues>
      initialValues={{ type: null, ...(formValuesRef.current || {}) }}
      onSubmit={onSubmit}
    >
      {({ isSubmitting }) => (
        <Form>
          <ScrollToError />
          <Field
            name="type"
            label={t({ id: 'property_valuation.planning_project_type_label' })}
            validate={(type: string | null) => (!type ? t({ id: 'errors.fill_field' }) : null)}
            options={[
              {
                img: BuyingHouse,
                label: t({ id: 'property_valuation.planned_project_types.buying_house' }),
                description: t({
                  id: 'property_valuation.planned_project_types.buying_house_description',
                }),
                value: 'buying_house',
              },
              {
                img: BuildingHouse,
                label: t({ id: 'property_valuation.planned_project_types.new_building' }),
                description: t({
                  id: 'property_valuation.planned_project_types.new_building_description',
                }),
                value: 'new_building',
              },
            ]}
            component={InlineSelect}
          />
          <div className="text-center">
            <Button
              loading={isSubmitting}
              disabled={isSubmitting}
              type="submit"
              endIcon={<ArrowForward />}
            >
              {t({ id: 'property_valuation.plan_project_btn' })}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  </Modal>
);

export default memo(LandValuationTypeModal, equal);
